import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { billingAPI } from '../lib/api';
import { CreditCard, Crown, Check, AlertCircle, Download, ArrowUpRight, Copy, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BillingPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await billingAPI.get();
        setData(res.data);
      } catch { toast.error('Failed to load billing info'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success('Transaction ID copied');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadInvoice = async (txId) => {
    try {
      const res = await billingAPI.downloadInvoice(txId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${txId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download invoice'); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  const sub = data?.subscription;
  const transactions = data?.transactions || [];
  const isFreePlan = sub?.plan_key === 'free';
  const isActive = sub?.status === 'active';
  const isExpired = sub?.status === 'expired';

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="billing-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Billing & Subscription</h1>
        <p className="text-[#5F6368] mt-1">Manage your monthly subscription and download invoices</p>
      </div>

      {/* Current Subscription Card */}
      <div className="bg-white rounded-2xl border border-[#E8EAED] overflow-hidden" data-testid="subscription-card">
        <div className={`px-6 py-4 ${isActive ? 'bg-gradient-to-r from-[#1A73E8] to-[#4285F4]' : isExpired ? 'bg-gradient-to-r from-[#D93025] to-[#EA4335]' : 'bg-gradient-to-r from-[#5F6368] to-[#9AA0A6]'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-lg font-semibold text-white">{sub?.plan_name} Plan</h2>
                <p className="text-sm text-white/80">Monthly Subscription</p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-white text-[#D93025]'}`}>
              {isActive ? 'ACTIVE' : 'EXPIRED'}
            </span>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {!isFreePlan && sub?.start_date && (
              <div>
                <p className="text-xs text-[#9AA0A6] flex items-center gap-1"><Calendar className="w-3 h-3" /> Subscription Date</p>
                <p className="text-sm font-medium text-[#202124] mt-1">{new Date(sub.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
              </div>
            )}
            {!isFreePlan && sub?.expiry_date && (
              <div>
                <p className="text-xs text-[#9AA0A6] flex items-center gap-1"><Clock className="w-3 h-3" /> Expiry Date</p>
                <p className={`text-sm font-medium mt-1 ${isExpired ? 'text-[#D93025]' : 'text-[#202124]'}`}>{new Date(sub.expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
              </div>
            )}
            {!isFreePlan && sub?.days_remaining !== null && (
              <div>
                <p className="text-xs text-[#9AA0A6]">Days Remaining</p>
                <p className={`text-sm font-medium mt-1 ${sub.days_remaining <= 5 ? 'text-[#D93025]' : 'text-[#1E8E3E]'}`}>{sub.days_remaining} days</p>
              </div>
            )}
            <div>
              <p className="text-xs text-[#9AA0A6]">Price</p>
              <p className="text-sm font-medium text-[#202124] mt-1">
                {isFreePlan ? 'Free' : `$${sub?.price_usd}/mo | ₹${sub?.price_inr}/mo`}
              </p>
            </div>
          </div>

          {/* Plan Limits */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-5 border-t border-[#F1F3F4]">
            <div className="bg-[#F8F9FA] rounded-lg p-3">
              <p className="text-xs text-[#9AA0A6]">Posts/Month</p>
              <p className="text-base font-semibold text-[#202124]">{sub?.max_posts_month >= 9999 ? 'Unlimited' : sub?.max_posts_month}</p>
            </div>
            <div className="bg-[#F8F9FA] rounded-lg p-3">
              <p className="text-xs text-[#9AA0A6]">Replies/Month</p>
              <p className="text-base font-semibold text-[#202124]">{sub?.max_replies_month >= 9999 ? 'Unlimited' : sub?.max_replies_month}</p>
            </div>
            <div className="bg-[#F8F9FA] rounded-lg p-3">
              <p className="text-xs text-[#9AA0A6]">Locations</p>
              <p className="text-base font-semibold text-[#202124]">{sub?.max_locations}</p>
            </div>
            <div className="bg-[#F8F9FA] rounded-lg p-3">
              <p className="text-xs text-[#9AA0A6]">Auto Features</p>
              <p className="text-base font-semibold text-[#202124]">{sub?.auto_post ? <Check className="w-5 h-5 text-[#1E8E3E] inline" /> : <AlertCircle className="w-5 h-5 text-[#DADCE0] inline" />}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-5">
            {isFreePlan && (
              <Link to="/pricing" className="btn-google flex items-center gap-2" data-testid="upgrade-btn">
                <ArrowUpRight className="w-4 h-4" /> Upgrade Plan
              </Link>
            )}
            {!isFreePlan && isActive && (
              <Link to="/pricing" className="btn-google flex items-center gap-2" data-testid="renew-btn">
                <ArrowUpRight className="w-4 h-4" /> Renew Subscription
              </Link>
            )}
            {!isFreePlan && isExpired && (
              <Link to="/pricing" className="bg-[#D93025] hover:bg-[#C5221F] text-white px-5 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2" data-testid="renew-expired-btn">
                <ArrowUpRight className="w-4 h-4" /> Renew Now
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-2xl border border-[#E8EAED]" data-testid="transaction-history">
        <div className="px-6 py-4 border-b border-[#F1F3F4]">
          <h2 className="text-lg font-semibold text-[#202124] flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#1A73E8]" /> Payment History
          </h2>
        </div>

        {transactions.length === 0 ? (
          <div className="text-center py-12 text-[#9AA0A6]">
            <CreditCard className="w-10 h-10 mx-auto mb-3 text-[#DADCE0]" />
            <p>No completed transactions</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8F9FA]">
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#9AA0A6] uppercase">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#9AA0A6] uppercase">Plan</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#9AA0A6] uppercase">Amount</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#9AA0A6] uppercase">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-[#9AA0A6] uppercase">Transaction ID</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-[#9AA0A6] uppercase">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const txId = tx.session_id || tx.order_id || tx.id || '';
                  return (
                    <tr key={tx.id} className="border-t border-[#F1F3F4] hover:bg-[#FAFAFA]">
                      <td className="py-4 px-4 text-sm text-[#5F6368]">{new Date(tx.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                      <td className="py-4 px-4">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                          tx.plan === 'pro' ? 'bg-[#F3E8FD] text-[#9334E9]' : 'bg-[#E8F0FE] text-[#1A73E8]'
                        }`}>{tx.plan_name || tx.plan?.toUpperCase()}</span>
                      </td>
                      <td className="py-4 px-4 text-sm font-semibold text-[#202124]">
                        {tx.currency?.toUpperCase() === 'INR' ? '₹' : '$'}{tx.amount}
                      </td>
                      <td className="py-4 px-4">
                        <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-[#E6F4EA] text-[#1E8E3E]">Active</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-[#5F6368] font-mono">{txId.slice(0, 16)}...</span>
                          <button onClick={() => handleCopyId(txId)} className="p-1 text-[#9AA0A6] hover:text-[#1A73E8]" title="Copy ID" data-testid={`copy-tx-${tx.id}`}>
                            {copiedId === txId ? <Check className="w-3.5 h-3.5 text-[#1E8E3E]" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <button
                          onClick={() => handleDownloadInvoice(tx.id)}
                          className="inline-flex items-center gap-1 text-xs text-[#1A73E8] hover:underline font-medium"
                          data-testid={`download-invoice-${tx.id}`}
                        >
                          <Download className="w-3.5 h-3.5" /> PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
